import { useState } from 'react';
import {
  ChevronDown,
  Cloud,
  Github,
  Key,
  ListTodo,
  Lock,
  Mail,
  MessageSquare,
  Pencil,
  PenTool,
  Plus,
  Shield,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date';
import { useCanModifyEmployee, useCanReadAccessTypes } from '@/lib/permissions';
import type { EmployeeDetail } from '@/types/employee';
import type { AccessGrant, AccessGrantStatus } from '@hygge/shared';
import {
  useEmployeeAccessGrants,
  useCreateAccessGrant,
  useUpdateAccessGrant,
  useRevokeAccessGrant,
  useDeleteAccessGrant,
} from '@/hooks/use-access-grants';
import { useAccessTypes } from '@/hooks/use-access-types';
import { IconActionButton } from './icon-action-button';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';

const ACCESS_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  github: Github,
  'message-square': MessageSquare,
  mail: Mail,
  cloud: Cloud,
  'list-todo': ListTodo,
  'pen-tool': PenTool,
  lock: Lock,
  shield: Shield,
};

function getAccessIcon(name?: string | null) {
  return (name && ACCESS_ICON_MAP[name]) || Key;
}

function getStatusBadge(status: AccessGrantStatus) {
  switch (status) {
    case 'ACTIVE':
      return <Badge variant="success">Active</Badge>;
    case 'REVOKED':
      return <Badge variant="default">Revoked</Badge>;
    case 'SUSPENDED':
      return <Badge variant="warning">Suspended</Badge>;
    case 'EXPIRED':
      return <Badge variant="destructive">Expired</Badge>;
    default:
      return <Badge variant="default">{status}</Badge>;
  }
}

function getRoleBadge(role: string | null) {
  if (!role) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
      {role}
    </span>
  );
}

interface AccessTabProps {
  employee: EmployeeDetail;
}

export function AccessTab({ employee }: AccessTabProps) {
  const { data: accessGrants, isLoading } = useEmployeeAccessGrants(employee.id);
  // Read only by a holder of an Access code — the route's rule. [HW-942]
  const { data: accessTypes } = useAccessTypes(false, { enabled: useCanReadAccessTypes() });
  const createGrant = useCreateAccessGrant();
  const updateGrant = useUpdateAccessGrant(employee.id);
  const revokeGrant = useRevokeAccessGrant(employee.id);
  const deleteGrant = useDeleteAccessGrant(employee.id);
  const canModify = useCanModifyEmployee();

  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGrant, setEditingGrant] = useState<AccessGrant | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editAccountEmail, setEditAccountEmail] = useState('');
  const [editIdentifier, setEditIdentifier] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [revokedOpen, setRevokedOpen] = useState(false);

  // Form state
  const [formAccessTypeId, setFormAccessTypeId] = useState('');
  const [formAccountEmail, setFormAccountEmail] = useState(employee.workEmail || '');
  const [formIdentifier, setFormIdentifier] = useState('');
  const [formRole, setFormRole] = useState('');
  const [formGrantedAt, setFormGrantedAt] = useState<Date | null>(new Date());
  const [formExpiresAt, setFormExpiresAt] = useState<Date | null>(null);
  const [formNotes, setFormNotes] = useState('');
  const [formRoleError, setFormRoleError] = useState<string | null>(null);

  const resetForm = () => {
    setFormAccessTypeId('');
    setFormAccountEmail(employee.workEmail || '');
    setFormIdentifier('');
    setFormRole('');
    setFormGrantedAt(new Date());
    setFormExpiresAt(null);
    setFormNotes('');
    setFormRoleError(null);
  };

  const handleCreate = async () => {
    if (!formAccessTypeId) return;
    if (!formRole.trim()) {
      setFormRoleError('Role is required');
      return;
    }
    await createGrant.mutateAsync({
      accessTypeId: formAccessTypeId,
      employeeId: employee.id,
      accountEmail: formAccountEmail || undefined,
      identifier: formIdentifier || undefined,
      role: formRole.trim(),
      grantedAt: formGrantedAt ? formGrantedAt.toISOString() : undefined,
      expiresAt: formExpiresAt ? formExpiresAt.toISOString() : undefined,
      notes: formNotes || undefined,
    });
    setGrantDialogOpen(false);
    resetForm();
  };

  const openEditDialog = (grant: AccessGrant) => {
    setEditingGrant(grant);
    setEditRole(grant.role || '');
    setEditAccountEmail(grant.accountEmail || '');
    setEditIdentifier(grant.identifier || '');
    setEditNotes(grant.notes || '');
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingGrant || !editRole.trim()) return;
    await updateGrant.mutateAsync({
      id: editingGrant.id,
      role: editRole.trim(),
      accountEmail: editAccountEmail || null,
      identifier: editIdentifier || null,
      notes: editNotes || null,
    });
    setEditDialogOpen(false);
    setEditingGrant(null);
  };

  const handleRevoke = async (grantId: string) => {
    await revokeGrant.mutateAsync(grantId);
  };

  const handleDelete = async (grantId: string) => {
    await deleteGrant.mutateAsync(grantId);
    setDeleteDialogOpen(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Access Grants
          </h3>
        </div>
        <div className="p-5">
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const allGrants = accessGrants || [];
  const activeGrants = allGrants.filter((g) => g.status === 'ACTIVE');
  const otherGrants = allGrants.filter((g) => g.status !== 'ACTIVE');

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Access Grants
          </h3>
          {canModify && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setGrantDialogOpen(true)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Grant Access
            </Button>
          )}
        </div>
        <div className="p-5">
          {allGrants.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                <Key className="h-6 w-6 text-muted-foreground/60" />
              </div>
              <h4 className="mt-4 text-sm font-medium text-foreground">No Access Grants</h4>
              <p className="mt-1 text-xs text-muted-foreground">
                Grant access to tools and services for this employee
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Active grants */}
              {activeGrants.length > 0 && (
                <div className="space-y-2">
                  {activeGrants.map((grant) => {
                    const Icon = getAccessIcon(grant.accessType?.icon);
                    return (
                      <div
                        key={grant.id}
                        className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-4 py-3"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                          <Icon className="h-4.5 w-4.5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {grant.accessType?.name || 'Unknown'}
                            </span>
                            {getRoleBadge(grant.role)}
                            {getStatusBadge(grant.status)}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                            {grant.accountEmail && (
                              <span className="truncate">{grant.accountEmail}</span>
                            )}
                            {grant.accountEmail && <span className="text-border">|</span>}
                            <span>
                              Granted{' '}
                              {formatDateTime(grant.grantedAt, {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              }) || '-'}
                            </span>
                            {grant.expiresAt && (
                              <>
                                <span className="text-border">|</span>
                                <span>
                                  Expires{' '}
                                  {formatDateTime(grant.expiresAt, {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  }) || '-'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        {canModify && (
                          <div className="flex items-center gap-1">
                            <IconActionButton
                              icon={Pencil}
                              tooltip="Edit"
                              onClick={() => openEditDialog(grant)}
                              size="sm"
                            />
                            <IconActionButton
                              icon={XCircle}
                              tooltip="Revoke access"
                              onClick={() => handleRevoke(grant.id)}
                              disabled={revokeGrant.isPending}
                              size="sm"
                            />
                            <IconActionButton
                              icon={Trash2}
                              tooltip="Delete"
                              onClick={() => setDeleteDialogOpen(grant.id)}
                              variant="destructive"
                              size="sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Revoked / Other grants */}
              {otherGrants.length > 0 && (
                <Collapsible open={revokedOpen} onOpenChange={setRevokedOpen}>
                  <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/40 transition-colors cursor-pointer">
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        revokedOpen && 'rotate-180'
                      )}
                    />
                    Revoked / Other ({otherGrants.length})
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-2 space-y-2">
                      {otherGrants.map((grant) => {
                        const Icon = getAccessIcon(grant.accessType?.icon);
                        return (
                          <div
                            key={grant.id}
                            className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/10 px-4 py-3 opacity-70"
                          >
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                              <Icon className="h-4.5 w-4.5 text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground truncate">
                                  {grant.accessType?.name || 'Unknown'}
                                </span>
                                {getRoleBadge(grant.role)}
                                {getStatusBadge(grant.status)}
                              </div>
                              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                                {grant.accountEmail && (
                                  <span className="truncate">{grant.accountEmail}</span>
                                )}
                                {grant.accountEmail && <span className="text-border">|</span>}
                                <span>
                                  Granted{' '}
                                  {formatDateTime(grant.grantedAt, {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  }) || '-'}
                                </span>
                                {grant.revokedAt && (
                                  <>
                                    <span className="text-border">|</span>
                                    <span>
                                      Revoked{' '}
                                      {formatDateTime(grant.revokedAt, {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                      }) || '-'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            {canModify && (
                              <IconActionButton
                                icon={Trash2}
                                tooltip="Delete"
                                onClick={() => setDeleteDialogOpen(grant.id)}
                                variant="destructive"
                                size="sm"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </div>
      </div>

      <CustomBlocksSection section="ACCESS" employeeId={employee.id} />

      {/* Grant Access Dialog */}
      <Dialog open={grantDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setGrantDialogOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Access</DialogTitle>
            <DialogDescription>
              Grant access to a tool or service for {employee.firstName} {employee.lastName}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="access-type">Access Type</Label>
              <Select value={formAccessTypeId} onValueChange={setFormAccessTypeId}>
                <SelectTrigger id="access-type">
                  <SelectValue placeholder="Select access type" />
                </SelectTrigger>
                <SelectContent>
                  {accessTypes?.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="account-email">Account Email</Label>
              <Input
                id="account-email"
                type="email"
                placeholder="e.g. user@service.com"
                value={formAccountEmail}
                onChange={(e) => setFormAccountEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="access-identifier">Identifier</Label>
              <Input
                id="access-identifier"
                placeholder="e.g. Project Alpha, Workspace Beta"
                value={formIdentifier}
                onChange={(e) => setFormIdentifier(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="access-role">
                Role <span className="text-destructive">*</span>
              </Label>
              <Input
                id="access-role"
                placeholder="e.g. Admin, Member, Viewer"
                required
                value={formRole}
                onChange={(e) => {
                  setFormRole(e.target.value);
                  if (formRoleError) setFormRoleError(null);
                }}
                aria-invalid={Boolean(formRoleError)}
                aria-describedby={formRoleError ? 'access-role-error' : undefined}
                className={cn(
                  formRoleError && 'border-destructive focus-visible:ring-destructive/30',
                )}
              />
              {formRoleError && (
                <p id="access-role-error" className="text-xs text-destructive">
                  {formRoleError}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Granted Date</Label>
              <DatePicker
                value={formGrantedAt}
                onChange={setFormGrantedAt}
                placeholder="Select granted date"
              />
            </div>
            <div className="grid gap-2">
              <Label>Expiry Date (optional)</Label>
              <DatePicker
                value={formExpiresAt}
                onChange={setFormExpiresAt}
                placeholder="Select expiry date"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="access-notes">Notes</Label>
              <Textarea
                id="access-notes"
                placeholder="Optional notes..."
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setGrantDialogOpen(false); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formAccessTypeId || !formRole.trim() || createGrant.isPending}
            >
              {createGrant.isPending ? 'Granting...' : 'Grant Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Access Grant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) { setEditDialogOpen(false); setEditingGrant(null); } }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Edit Access Grant</DialogTitle>
            <DialogDescription>
              Update access details for {editingGrant?.accessType?.name || 'this service'}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-account-email">Account Email</Label>
              <Input
                id="edit-account-email"
                type="email"
                placeholder="e.g. user@service.com"
                value={editAccountEmail}
                onChange={(e) => setEditAccountEmail(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-identifier">Identifier</Label>
              <Input
                id="edit-identifier"
                placeholder="e.g. Project Alpha, Workspace Beta"
                value={editIdentifier}
                onChange={(e) => setEditIdentifier(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-role">
                Role <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-role"
                placeholder="e.g. Admin, Member, Viewer"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                placeholder="Optional notes..."
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingGrant(null); }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={!editRole.trim() || updateGrant.isPending}
            >
              {updateGrant.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDialogOpen} onOpenChange={(open) => { if (!open) setDeleteDialogOpen(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Access Grant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this access grant record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deleteDialogOpen && handleDelete(deleteDialogOpen)}
              disabled={deleteGrant.isPending}
            >
              {deleteGrant.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
